import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Play, Star, ArrowRight } from "lucide-react";
import { GoogleLogo } from "@/components/GoogleLogo";
import { ClientPlyr as Plyr } from "@/components/ClientPlyr";
import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { testimoniosData } from "@/components/TestimoniosCarousel";
import { GoogleReviewsWidget } from "@/components/GoogleReviewsWidget";
import { Link } from "@/lib/router-compat";

const Testimonios = () => {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const videoTestimonials = testimoniosData.filter((t) => t.videoUrl);
  const textTestimonials = testimoniosData.filter((t) => !t.videoUrl);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Testimonios de clientes — CreceLocal"
        description="Videotestimonios y reseñas reales de negocios argentinos que usan CreceLocal para automatizar su proceso comercial. Distribuidoras, corralones, clínicas y comercios."
        canonical="/testimonios"
      />
      <Navbar />
      <main>
        {/* HERO */}
        <section className="pt-32 pb-12 md:pt-40 md:pb-16">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <p className="text-[#71717A] font-mono text-[13px] tracking-[0.08em] uppercase mb-6">
              ● LO QUE DICEN NUESTROS CLIENTES
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-medium tracking-tight text-foreground leading-[1.05] mb-6">
              Negocios reales.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A78BFA] to-[#FB7185]">
                Resultados reales.
              </span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Distribuidoras, corralones, clínicas, comercios y servicios
              profesionales de toda la Argentina que ya tienen su proceso
              comercial funcionando solo.
            </p>
          </div>
        </section>

        {/* VIDEO TESTIMONIALS */}
        <section className="container mx-auto px-6 max-w-[1200px] mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {videoTestimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="bg-[#0F0F11] border border-white/5 rounded-[20px] p-8 lg:p-10 flex flex-col justify-between hover:border-white/10 transition-all duration-300 relative overflow-hidden group"
                style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}
              >
                <div className="relative z-10 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-8">
                    <div className="w-14 h-14 rounded-full bg-[#131316] border border-white/10 flex items-center justify-center">
                      <span className="text-[#FAFAFA] font-medium text-[16px] tracking-wider">
                        {testimonial.initials}
                      </span>
                    </div>
                    <GoogleLogo className="w-6 h-6" />
                  </div>

                  <Dialog
                    onOpenChange={(open) => !open && setActiveVideo(null)}
                  >
                    <DialogTrigger asChild>
                      <div
                        className="relative w-full h-48 mb-8 rounded-[12px] overflow-hidden cursor-pointer group/vid border border-white/10 bg-[#08080A] shrink-0"
                        onClick={() => setActiveVideo(testimonial.videoUrl)}
                      >
                        <video
                          src={`${testimonial.videoUrl!}#t=0.001`}
                          className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover/vid:opacity-70 transition-opacity duration-300"
                          muted
                          playsInline
                          preload="metadata"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover/vid:bg-black/20 transition-colors duration-300">
                          <div className="w-14 h-14 rounded-full bg-[#7C3AED]/20 flex items-center justify-center backdrop-blur-md border border-[#7C3AED]/30 group-hover/vid:scale-110 transition-transform duration-300">
                            <Play className="w-6 h-6 text-[#7C3AED] ml-1 fill-current" />
                          </div>
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl bg-black border-white/10 p-0 overflow-hidden">
                      <DialogTitle className="sr-only">
                        Video de testimonio {testimonial.name}
                      </DialogTitle>
                      <DialogDescription className="sr-only">
                        Testimonio en video de {testimonial.name} de{" "}
                        {testimonial.role}
                      </DialogDescription>
                      {activeVideo === testimonial.videoUrl && (
                        <div className="relative w-full aspect-video [&_.plyr]:h-full [&_.plyr__video-wrapper]:h-full [&_video]:object-contain bg-black">
                          <Plyr
                            source={{
                              type: "video",
                              sources: [
                                {
                                  src: testimonial.videoUrl!,
                                  type: "video/mp4",
                                },
                              ],
                            }}
                            options={{
                              autoplay: true,
                              controls: [
                                "play-large",
                                "play",
                                "progress",
                                "current-time",
                                "mute",
                                "volume",
                                "fullscreen",
                              ],
                            }}
                          />
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>

                  <div className="relative mb-8 flex-grow">
                    <span className="absolute -top-4 -left-4 text-[#7C3AED]/20 text-[60px] font-serif leading-none select-none">
                      "
                    </span>
                    <p className="text-[#FAFAFA] text-[20px] lg:text-[22px] leading-[1.5] relative z-10 font-normal">
                      {testimonial.quote}
                    </p>
                  </div>
                </div>

                <div className="relative z-10 mt-auto">
                  <div className="mb-6">
                    <p className="text-[#FAFAFA] font-medium text-[16px]">
                      {testimonial.name}
                    </p>
                    <p className="text-[#A1A1AA] text-[14px]">
                      {testimonial.role}
                    </p>
                  </div>
                  <div className="inline-flex items-center px-3 py-1.5 rounded-[6px] bg-[#34D399]/10 border border-[#34D399]/20">
                    <span className="text-[#34D399] font-mono text-[11px] font-bold tracking-[0.08em] uppercase">
                      {testimonial.metric}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* TEXT TESTIMONIALS */}
        {textTestimonials.length > 0 && (
          <section className="container mx-auto px-6 max-w-[1200px] mb-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {textTestimonials.map((testimonial, idx) => (
                <div
                  key={idx}
                  className="bg-[#0F0F11] border border-white/5 rounded-[20px] p-6 lg:p-8 flex flex-col justify-between hover:border-white/10 transition-all duration-300 relative overflow-hidden"
                  style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}
                >
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-12 h-12 rounded-full bg-[#131316] border border-white/10 flex items-center justify-center">
                        <span className="text-[#FAFAFA] font-medium text-[14px] tracking-wider">
                          {testimonial.initials}
                        </span>
                      </div>
                      <GoogleLogo className="w-5 h-5" />
                    </div>

                    <div className="flex gap-1 mb-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className="w-3.5 h-3.5 fill-[#FBBF24] text-[#FBBF24]"
                        />
                      ))}
                    </div>

                    <div className="relative mb-6 flex-grow">
                      <span className="absolute -top-4 -left-4 text-[#7C3AED]/20 text-[48px] font-serif leading-none select-none">
                        "
                      </span>
                      <p className="text-[#FAFAFA] text-[15px] lg:text-[16px] leading-[1.5] relative z-10 font-normal">
                        {testimonial.quote}
                      </p>
                    </div>
                  </div>

                  <div className="relative z-10 mt-auto">
                    <div className="mb-4">
                      <p className="text-[#FAFAFA] font-medium text-[14px]">
                        {testimonial.name}
                      </p>
                      <p className="text-[#A1A1AA] text-[12px]">
                        {testimonial.role}
                      </p>
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-[6px] bg-[#34D399]/10 border border-[#34D399]/20">
                      <span className="text-[#34D399] font-mono text-[10px] font-bold tracking-[0.08em] uppercase">
                        {testimonial.metric}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* RESULT FRANJA */}
        <section className="container mx-auto px-6 max-w-3xl mb-20">
          <div className="bg-[#0F0F12] border border-white/10 rounded-2xl p-6 md:p-8 text-center">
            <p className="text-base md:text-lg text-foreground leading-relaxed">
              En un corralón de materiales el sistema completo generó{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A78BFA] to-[#FB7185] font-bold">
                319 oportunidades y 55 ventas en 20 días.
              </span>{" "}
              Hasta ahí llega esto cuando el negocio tiene volumen.
            </p>
          </div>
        </section>

        {/* GOOGLE REVIEWS WIDGET */}
        <section className="container mx-auto px-6 max-w-[1100px] mb-20">
          <GoogleReviewsWidget title="Más reseñas de clientes" />
        </section>

        {/* CTA */}
        <section className="container mx-auto px-6 max-w-3xl text-center mb-20">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-foreground mb-4">
            ¿Querés ver cómo funciona para tu negocio?
          </h2>
          <p className="text-muted-foreground text-base mb-8">
            Agendá una llamada y te mostramos cuentas reales andando.
          </p>
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium rounded-full px-7 py-3.5 text-sm hover:bg-primary/90 transition-all group"
          >
            Agendar una llamada
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Testimonios;
